import { useMemo } from 'react';
import { SegmentationRule } from '../Modal/SegmentationModal';
import { TFunction } from 'i18next';
import styled from 'styled-components';
import { P } from '@salecycle/ui-component-library';
import { getSegmentRuleDisplayValue } from './getSegmentRuleDisplayValue';

const SegmentListStyle = styled.ul<{ useMargins: boolean }>`
  margin: ${({ theme, useMargins }) => (useMargins ? theme.designToken.spacing3 : 0)} 0;
  list-style-position: inside;

  & p {
    display: inline;
  }
`;

const ConditionItem = styled.div`
  font: ${({ theme }) => theme.designToken.body2Semibold};
  color: ${({ theme }) => theme.designToken.neutral700};
`;

const RuleListItem = styled.div<{ logic: string }>`
  display: flex;
  flex-direction: ${({ logic }) => (logic === 'or' ? 'row' : 'column')};
`;

const RuleItem = styled.div`
  display: list-item;
  margin-left: ${({ theme }) => theme.designToken.spacing2};
`;

const OR = styled.div`
  margin-left: ${({ theme }) => theme.designToken.spacing1};
  display: inline-block;
`;

const EllipsisText = styled.strong`
  display: inline-block;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

type MappedSegmentRule = SegmentationRule<any, any, any> & {
  or: boolean;
};

export interface SelectedSegmentationRulesProps {
  rules: SegmentationRule<any, any, any>[][];
  t: TFunction;
  useMargins?: boolean;
  getRuleLabels?: (rule: MappedSegmentRule) => { type: string; condition: string; value: string };
}

const getLabels = (
  rule: MappedSegmentRule,
  t: TFunction,
  getOffsiteLanguagePack: SelectedSegmentationRulesProps['getRuleLabels'],
) => {
  if (getOffsiteLanguagePack) {
    return getOffsiteLanguagePack(rule);
  }

  return {
    type: t(`onsite.segmentationRules.types.${rule.type}`),
    condition: t(`onsite.segmentationRules.conditions.${rule.configuration?.condition}`),
    value: getSegmentRuleDisplayValue(rule, t),
  };
};

export const SelectedSegmentationRules = ({
  rules,
  t,
  useMargins,
  getRuleLabels: getOffsiteLanguagePack,
}: SelectedSegmentationRulesProps) => {
  const segmentationRules = useMemo(
    () =>
      rules
        ?.map(segments => {
          return segments.map((segment, index) => {
            if (segments.length > 1 && index !== segments.length - 1) {
              return { ...segment, or: true };
            }
            return { ...segment, or: false };
          });
        })
        .flatMap(s => s),
    [rules],
  );

  return (
    <SegmentListStyle data-testid="segmentationRule" useMargins={useMargins}>
      {segmentationRules?.map((s, index) => {
        const labels = getLabels(s, t, getOffsiteLanguagePack);

        return (
          <RuleListItem key={index} logic={s.or ? 'or' : 'and'}>
            <RuleItem>
              <P style={{ display: 'inline' }}>
                {`${labels.type} ${s.configuration?.condition ? labels.condition : ''} `}
                <EllipsisText>{labels.value}</EllipsisText>
              </P>
            </RuleItem>

            {index !== segmentationRules.length - 1 && (
              <ConditionItem>
                {s.or ? (
                  <>
                    {','}
                    <OR>{t('onsite.segmentationRules.logic.or')}</OR>
                  </>
                ) : (
                  t(`onsite.segmentationRules.logic.and`)
                )}
              </ConditionItem>
            )}
          </RuleListItem>
        );
      })}
    </SegmentListStyle>
  );
};

SelectedSegmentationRules.defaultProps = {
  useMargins: false,
};
